import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3 p-4 rounded-lg bg-[#1f2937] border border-gray-700" }
const _hoisted_2 = { class: "flex flex-row items-center justify-between" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h3", { class: "text-sm font-semibold text-white" }, "Scanner un code QR", -1)
const _hoisted_4 = {
  key: 0,
  class: "text-xs text-gray-400 text-center"
}
const _hoisted_5 = {
  key: 1,
  class: "text-xs text-green-400 text-center"
}
const _hoisted_6 = {
  key: 2,
  class: "text-xs text-orange-300 text-center"
}
const _hoisted_7 = {
  key: 3,
  class: "flex flex-col gap-2 pt-2 border-t border-gray-700"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("label", { class: "text-xs text-gray-400" }, "Ou saisissez le code manuellement", -1)
const _hoisted_9 = { class: "flex flex-row gap-2" }
const _hoisted_10 = ["disabled"]

import { ref, onMounted, onBeforeUnmount } from "vue";

	const containerId = "jahagal-qr-scanner";
	const SCAN_DEBOUNCE_MS = 2000;

	
export default /*#__PURE__*/_defineComponent({
  __name: 'QrScannerTag',
  props: {
    showManualFallback: { type: Boolean }
  },
  emits: ["scan", "close"],
  setup(__props: any, { emit: __emit }) {

	/**
	 * Composant scanner QR — utilise html5-qrcode (lib ~50 KB gzip)
	 * en lazy import pour ne pas charger la caméra tant que la modale
	 * n'est pas ouverte.
	 *
	 * Usage typique :
	 *
	 *   <QrScannerTag
	 *     v-if="showScanner"
	 *     @scan="handleScan"
	 *     @close="showScanner = false"
	 *   />
	 *
	 * Le composant gère :
	 *   - demande de permission caméra
	 *   - fallback "saisie manuelle UUID" si caméra refusée ou non dispo
	 *   - feedback visuel (cadre, statut, dernier scan)
	 *   - debounce pour ne pas émettre 10× le même payload
	 *
	 * Pré-requis package.json : "html5-qrcode": "^2.3.8"
	 *
	 * Activé par feature.operations.qrcode_scan (déjà existant) côté
	 * enterprise. Pas de gate côté composant (la gate se fait en amont
	 * dans le composant qui décide d'afficher le bouton "Scanner").
	 */
	const emit = __emit;

	const props = __props;

	const scannerInstance = ref<any>(null);
	const cameraStatus = ref<"loading" | "active" | "denied" | "unsupported" | "error">("loading");
	const errorMessage = ref<string>("");
	const lastScannedAt = ref(0);
	const lastScannedPayload = ref<string>("");
	const manualInput = ref<string>("");

	// Debounce : on évite d'émettre 10× le même payload si la caméra
	// le détecte en continu. Fenêtre 2s.
	async function startScanner() {
		cameraStatus.value = "loading";
		errorMessage.value = "";
		try {
			// Lazy import : on ne charge la lib QR (50 KB) que si on
			// arrive vraiment à ouvrir le scanner. Évite la pénalité
			// au mount initial de l'app.
			const { Html5Qrcode } = await import("html5-qrcode");
			const cameras = await Html5Qrcode.getCameras();
			if (!cameras || cameras.length === 0) {
				cameraStatus.value = "unsupported";
				errorMessage.value = "Aucune caméra détectée sur cet appareil.";
				return;
			}
			// Préfère la caméra arrière si disponible (typique mobile).
			const preferred =
				cameras.find((c: any) => /back|rear|arrière/i.test(c.label)) || cameras[0];

			scannerInstance.value = new Html5Qrcode(containerId);
			await scannerInstance.value.start(
				preferred.id,
				{
					fps: 10,
					qrbox: { width: 240, height: 240 },
					aspectRatio: 1.0,
				},
				(decodedText: string) => onDecode(decodedText),
				// On ignore les frames sans QR (callback erreur)
				() => undefined,
			);
			cameraStatus.value = "active";
		} catch (err: any) {
			const msg = String(err?.message || err);
			// Permission refusée par l'utilisateur ou par la policy navigateur
			if (/NotAllowed|Permission/i.test(msg)) {
				cameraStatus.value = "denied";
				errorMessage.value =
					"Accès à la caméra refusé. Activez-le dans les réglages du navigateur ou saisissez le code à la main.";
			} else {
				cameraStatus.value = "error";
				errorMessage.value = msg || "Impossible de démarrer le scanner.";
			}
			console.error("[QrScannerTag] start failed", err);
		}
	}

	async function stopScanner() {
		try {
			if (scannerInstance.value?.isScanning) {
				await scannerInstance.value.stop();
			}
			scannerInstance.value?.clear?.();
		} catch (err) {
			console.warn("[QrScannerTag] stop failed", err);
		} finally {
			scannerInstance.value = null;
		}
	}

	function onDecode(payload: string) {
		const now = Date.now();
		// Debounce : même payload + < 2s = ignore
		if (
			payload === lastScannedPayload.value &&
			now - lastScannedAt.value < SCAN_DEBOUNCE_MS
		) {
			return;
		}
		lastScannedAt.value = now;
		lastScannedPayload.value = payload;
		emit("scan", payload);
	}

	function submitManual() {
		const v = manualInput.value.trim();
		if (!v) return;
		emit("scan", v);
		manualInput.value = "";
	}

	onMounted(() => {
		startScanner();
	});

	onBeforeUnmount(() => {
		stopScanner();
	});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("button", {
        type: "button",
        class: "text-gray-400 hover:text-white text-xs",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      }, "✕ Fermer")
    ]),
    _createElementVNode("div", {
      id: containerId,
      class: _normalizeClass(["w-full max-w-sm mx-auto rounded-md overflow-hidden bg-black", { 'opacity-50': cameraStatus.value !== 'active' }]),
      style: {"aspect-ratio":"1 / 1"}
    }, null, 2),
    (cameraStatus.value === 'loading')
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, "Démarrage de la caméra…"))
      : (cameraStatus.value === 'active')
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, "Visez le QR à scanner"))
        : (cameraStatus.value !== 'active')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true),
    (props.showManualFallback || cameraStatus.value === 'denied' || cameraStatus.value === 'unsupported' || cameraStatus.value === 'error')
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _hoisted_8,
          _createElementVNode("div", _hoisted_9, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((manualInput).value = $event)),
              type: "text",
              placeholder: "UUID du ticket ou payload QR…",
              class: "flex-1 px-3 py-2 text-sm rounded bg-[#0f172a] border border-gray-700 text-white",
              onKeydown: _withKeys(submitManual, ["enter"])
            }, null, 544), [
              [_vModelText, manualInput.value]
            ]),
            _createElementVNode("button", {
              type: "button",
              class: "px-4 py-2 text-sm rounded bg-[#219152] hover:bg-[#167841] text-white disabled:opacity-50",
              disabled: !manualInput.value.trim(),
              onClick: submitManual
            }, " Valider ", 8, _hoisted_10)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})